import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  ListItem,
  SimpleGrid,
  Spinner,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

import PageHeader from "../components/PageHeader";
import PoolCardScroller from "../components/PoolCardScroller";
import { useAuth } from "../auth/AuthContext";
import {
  createPickemPool,
  getAllPickemPools,
  getMyPickemPools,
  joinPickemPool,
} from "../api/nflPickem";
import { useNflSeason } from "../hooks/useNflSeason";
import nflLogo from "../assets/logos/nfl.png";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE = "#00007f";

export default function NflPickemHome() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { season, seasonId, loading: seasonLoading } = useNflSeason();
  // Three lobby states: too early, open for setup, season under way.
  const creationOpen = !!season?.pool_creation_open;
  const seasonStarted = !!season?.season_started;

  const [myPools, setMyPools] = useState([]);
  const [allPools, setAllPools] = useState([]);
  const [loadingPools, setLoadingPools] = useState(true);
  const [poolError, setPoolError] = useState("");

  const [newPoolName, setNewPoolName] = useState("");
  const [newPoolDepth, setNewPoolDepth] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [joiningPoolId, setJoiningPoolId] = useState(null);

  useEffect(() => {
    if (!seasonId) {
      if (!seasonLoading) setLoadingPools(false);
      return;
    }
    loadPools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonId, seasonLoading]);

  async function loadPools() {
    setLoadingPools(true);
    setPoolError("");
    try {
      const [mine, all] = await Promise.all([
        getMyPickemPools(seasonId),
        getAllPickemPools(seasonId),
      ]);
      setMyPools(mine);
      setAllPools(all);
    } catch (err) {
      setPoolError(err?.response?.data?.detail || "Unable to load pools.");
    } finally {
      setLoadingPools(false);
    }
  }

  const joinablePools = useMemo(() => {
    const mine = new Set(myPools.map((p) => p.id));
    return allPools.filter((p) => !mine.has(p.id) && !p.is_private);
  }, [myPools, allPools]);

  function handleOpenPool(poolId) {
    navigate(`/nfl-pickem/current/${poolId}`, { state: { seasonId } });
  }

  async function handleJoinPool(poolId) {
    setJoiningPoolId(poolId);
    setPoolError("");
    try {
      await joinPickemPool(seasonId, poolId);
      await loadPools();
      handleOpenPool(poolId);
    } catch (err) {
      setPoolError(err?.response?.data?.detail || "Failed to join pool.");
    } finally {
      setJoiningPoolId(null);
    }
  }

  async function handleCreatePool() {
    const name = newPoolName.trim();
    if (!name || !seasonId) return;
    setCreating(true);
    setCreateError("");
    try {
      const pool = await createPickemPool(seasonId, {
        name,
        pool_depth: newPoolDepth ? Number(newPoolDepth) : null,
      });
      setNewPoolName("");
      setNewPoolDepth("");
      await loadPools();
      navigate(`/nfl-pickem/setup/${pool.id}`, { state: { seasonId } });
    } catch (err) {
      setCreateError(err?.response?.data?.detail || "Failed to create pool.");
    } finally {
      setCreating(false);
    }
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white" overflowX="hidden">
      <PageHeader
        title="NFL PICK 'EM LOBBY"
        onBack={() => navigate("/")}
        user={user}
        onLogout={() => { logout(); navigate("/login", { replace: true }); }}
      />

      <Container maxW="container.xl" py={10}>
        <VStack align="stretch" spacing={8}>
          <VStack align="start" spacing={2}>
            <Text fontSize="lg" color="whiteAlpha.900">
              Welcome to the NFL Pick &apos;Em lobby. Enter your current game, view open
              pools, or start a new pool.
            </Text>
            {creationOpen && season && (
              <Alert status="success" borderRadius="md" mt={1} color="green.900">
                <AlertIcon flexShrink={0} />
                <Text ml={2}>
                  The <strong>{season.name}</strong> season is available for set up — create
                  your pool below!
                </Text>
              </Alert>
            )}
          </VStack>

          {poolError && (
            <Alert status="error" borderRadius="md"><AlertIcon />{poolError}</Alert>
          )}

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {/* Your Active Games */}
            <Box
              bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`}
              borderRadius="lg" boxShadow="lg" p={8} minH="260px" minW="0"
              position="relative" overflow="hidden"
              opacity={!loadingPools && myPools.length === 0 ? 0.5 : 1}
            >
              <Box position="absolute" inset={0} display="flex" alignItems="center"
                   justifyContent="center" opacity={0.06} pointerEvents="none" zIndex={0}>
                <img src={nflLogo} alt="" style={{ width: "260px" }} />
              </Box>

              <VStack align="stretch" spacing={4} h="100%" position="relative" zIndex={1}>
                <Heading size="md" color={TOP_BAR_BLUE}>Your Active Games</Heading>
                <Text color="gray.700">Pick &apos;Em pools you belong to this season.</Text>

                {loadingPools ? (
                  <Flex flex="1" align="center" justify="center"><Spinner /></Flex>
                ) : myPools.length === 0 ? (
                  <Flex flex="1" align="center" justify="center">
                    <Text fontSize="lg" color="gray.500" textAlign="center">
                      You&apos;re not in a pool yet.
                    </Text>
                  </Flex>
                ) : (
                  <PoolCardScroller>
                    {myPools.map((pool) => (
                      <Box key={pool.id} border="1px solid" borderColor="gray.200"
                           borderRadius="md" p={4} bg="gray.50">
                        <VStack align="center" spacing={2}>
                          <Text fontWeight="bold" textAlign="center">{pool.name}</Text>
                          <Text fontSize="sm" color="gray.600">
                            Players: {pool.user_count}
                          </Text>
                          {pool.total_pot != null && (
                            <Text fontSize="sm" color="gray.600">Total Pot: ${pool.total_pot}</Text>
                          )}
                          <HStack spacing={2} justify="center">
                            {(pool.owner_user_id === user?.id || pool.current_user_is_co_admin) && (
                              <Button size="sm" variant="outline" borderColor={TOP_BAR_BLUE}
                                      color={TOP_BAR_BLUE} _hover={{ bg: "gray.100" }}
                                      onClick={() => navigate(`/nfl-pickem/setup/${pool.id}`,
                                                              { state: { seasonId } })}>
                                Edit Pool
                              </Button>
                            )}
                            <Button size="sm" bg={TOP_BAR_BLUE} color="white"
                                    _hover={{ bg: "#00004a" }}
                                    onClick={() => handleOpenPool(pool.id)}>
                              Open Pool
                            </Button>
                          </HStack>
                        </VStack>
                      </Box>
                    ))}
                  </PoolCardScroller>
                )}
              </VStack>
            </Box>

            {/* Join an Open Pool */}
            <Box
              bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`}
              borderRadius="lg" boxShadow="lg" p={8} minH="260px" minW="0"
              position="relative" overflow="hidden"
              opacity={!loadingPools && joinablePools.length === 0 ? 0.5 : 1}
            >
              <Box position="absolute" inset={0} display="flex" alignItems="center"
                   justifyContent="center" opacity={0.06} pointerEvents="none" zIndex={0}>
                <img src={nflLogo} alt="" style={{ width: "260px" }} />
              </Box>

              <VStack align="stretch" spacing={4} h="100%" position="relative" zIndex={1}>
                <Heading size="md" color={TOP_BAR_BLUE}>Join an Open Pool</Heading>
                <Text color="gray.700">Available pools for the current season.</Text>

                {loadingPools ? (
                  <Flex flex="1" align="center" justify="center"><Spinner /></Flex>
                ) : joinablePools.length === 0 ? (
                  <Flex flex="1" align="center" justify="center">
                    <Text fontSize="lg" color="gray.500" textAlign="center">
                      No open pools available right now.
                    </Text>
                  </Flex>
                ) : (
                  <PoolCardScroller>
                    {joinablePools.map((pool) => {
                      const opensAt = pool.opens_at ? new Date(pool.opens_at) : null;
                      const notYetOpen = !!opensAt && opensAt > new Date();
                      return (
                        <Box key={pool.id} border="1px solid" borderColor="gray.200"
                             borderRadius="md" p={4} bg="gray.50">
                          <VStack align="center" spacing={2}>
                            <Text fontWeight="bold" textAlign="center">{pool.name}</Text>
                            <Text fontSize="sm" color="gray.600">
                              Pool Depth: {pool.pool_depth ?? "—"}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              Players: {pool.user_count}
                            </Text>
                            {pool.total_pot != null && (
                              <Text fontSize="sm" color="gray.600">Total Pot: ${pool.total_pot}</Text>
                            )}
                            {pool.is_open === false ? (
                              <Button size="sm" isDisabled>
                                {notYetOpen
                                  ? `Opens ${opensAt.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                                  : "Entries closed"}
                              </Button>
                            ) : (
                              <Button size="sm" bg={TOP_BAR_BLUE} color="white"
                                      _hover={{ bg: "#00004a" }}
                                      isLoading={joiningPoolId === pool.id}
                                      onClick={() => handleJoinPool(pool.id)}>
                                Join Pool
                              </Button>
                            )}
                          </VStack>
                        </Box>
                      );
                    })}
                  </PoolCardScroller>
                )}
              </VStack>
            </Box>
          </SimpleGrid>

          {/* Rules + New Pool */}
          <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`}
               borderRadius="lg" boxShadow="lg" p={8}>
            <VStack align="stretch" spacing={8}>
              {/* 1 — Want to Start a New Pool? */}
              <Box>
                <Heading size="md" mb={3} color={TOP_BAR_BLUE}>Want to Start a New Pool?</Heading>
                {seasonStarted && (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    New games can&apos;t be created mid season
                  </Alert>
                )}
              </Box>

              {/* 2 — Rules */}
              <Box>
                <Heading size="md" mb={3} color={TOP_BAR_BLUE}>Rules</Heading>
                <UnorderedList spacing={3} pl={5}>
                  <ListItem>
                    Pick <strong>one team each week</strong> and assign it <strong>1-5 confidence points.</strong>
                  </ListItem>
                  <ListItem>
                    Picks must be made before kickoff. Once a game kicks off, it is locked.
                  </ListItem>
                  <ListItem>
                    A team may only be picked <strong>once per season</strong>.
                  </ListItem>
                  <ListItem>
                    You may only pick <strong>against</strong> the same team a limited number of times — the pool owner sets the limit.
                  </ListItem>
                  <ListItem>
                    Miss a pick and you get the team with the worst odds at 5 confidence points.
                  </ListItem>
                  <ListItem>
                    Predict how every division finishes. Predictions lock before Week 4 and are scored on their own leaderboard.
                  </ListItem>
                </UnorderedList>
              </Box>

              <Divider borderColor="gray.300" />

              {/* 3 — Scoring */}
              <Box>
                <Heading size="md" mb={3} color={TOP_BAR_BLUE}>Scoring</Heading>
                <UnorderedList spacing={3} pl={5}>
                  <ListItem>
                    If your selected team wins, you earn the confidence points selected for the pick.
                  </ListItem>
                  <ListItem>
                    If your selected team loses, you lose the confidence points selected for the pick.
                  </ListItem>
                  <ListItem>
                    If your selected team ties, you receive no points for that matchweek pick.
                  </ListItem>
                  <ListItem>
                    <strong>Boat Race Bonus:</strong> win by 21 or more for an extra 2 points.
                  </ListItem>
                  <ListItem>
                    <strong>Underdog Bonus:</strong> back an underdog and double your
                    confidence points — limited uses per season.
                  </ListItem>
                  <ListItem>
                    Most points wins. Ties are broken by total wins, then point differential.
                  </ListItem>
                </UnorderedList>
              </Box>

              <Divider borderColor="gray.300" />

              {/* 4 — Pool Name / Depth / Button */}
              <Box opacity={creationOpen ? 1 : 0.5}>
                {createError && (
                  <Alert status="error" borderRadius="md" mb={4}>
                    <AlertIcon />{createError}
                  </Alert>
                )}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                  <FormControl isRequired isDisabled={!creationOpen}>
                    <FormLabel fontSize="sm">Pool Name</FormLabel>
                    <Input value={newPoolName} placeholder="Enter pool name"
                           onChange={(e) => { setNewPoolName(e.target.value); setCreateError(""); }} />
                  </FormControl>
                  <FormControl isDisabled={!creationOpen}>
                    <FormLabel fontSize="sm">Pool Depth ($)</FormLabel>
                    <Input type="number" min={1} value={newPoolDepth}
                           placeholder="Optional buy-in amount"
                           onChange={(e) => setNewPoolDepth(e.target.value)} />
                  </FormControl>
                </SimpleGrid>
                <Button bg={TOP_BAR_BLUE} color="white" _hover={{ bg: "#00004a" }}
                        isLoading={creating} loadingText="Creating..."
                        isDisabled={!seasonId || !creationOpen}
                        onClick={handleCreatePool}>
                  Start New Pool
                </Button>
                {!creationOpen && !seasonStarted && (
                  <Text mt={4} fontSize="sm" color="gray.600">
                    Once next season setup is available, this page will allow users to create a
                    new pool before the season begins.
                  </Text>
                )}
                {creationOpen && (
                  <Text mt={4} fontSize="sm" color="gray.600">
                    You&apos;ll set the pick-against limit, bonuses and payouts on the next screen.
                  </Text>
                )}
              </Box>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
